/**
 * GFG-439 — how much room a campaign has left under the plan's recipient cap. `null` is an
 * unlimited plan (Pro Plus, Enterprise): it has no room figure and never blocks. Values are passed
 * in; the caller decides where the cap and the current count come from.
 */

import { isRecipientCapRefusal } from "@/lib/entitlementError";

/** Recipients still addable under the cap, never negative. `null` when the plan is unlimited. */
export function recipientRoom(cap: number | null, current: number): number | null {
  if (cap === null) return null;
  return Math.max(0, cap - current);
}

/** True when the campaign has reached the cap. Unlimited plans are never at it. */
export function atRecipientCap(cap: number | null, current: number): boolean {
  if (cap === null) return false;
  return current >= cap;
}

/**
 * Post-launch add: does the "Contact limit reached" dialog replace the add flow? Not for a
 * replacement-only add (it grows nothing) nor a draft (the builder's own step gates it).
 */
export function capDialogOnAdd(input: {
  cap: number | null;
  current: number;
  replacementOnly: boolean;
  draft: boolean;
}): boolean {
  return !input.replacementOnly && !input.draft && atRecipientCap(input.cap, input.current);
}

/** A cap 402 counts only on a capped plan whose key is known (the dialog copy needs both). */
export function isCapRefusalFor(err: unknown, cap: number | null, planKey: string | null): boolean {
  return cap !== null && planKey !== null && isRecipientCapRefusal(err);
}

/** The blocked dialog opens after a cap 402 on the quote, or after a refused confirm. */
export function capRefusalOpen(input: {
  quoteError: unknown;
  confirmRefused: boolean;
  cap: number | null;
  planKey: string | null;
}): boolean {
  const { quoteError, confirmRefused, cap, planKey } = input;
  if (cap === null || planKey === null) return false;
  return confirmRefused || isCapRefusalFor(quoteError, cap, planKey);
}

/** A quantity fits the room left; `null` room is unlimited. */
export function quantityWithinRoom(quantity: number, room: number | null): boolean {
  return room === null || quantity <= room;
}
